import React from "react";
import { useSearchParams } from "react-router-dom";
import PageContainer from "../../../components/PageContainer";
import DataTable from "../../../components/DataTable";
import EmptyState from "../../../components/EmptyState";
import Pagination from "../../../components/Pagination";
import TableSkeleton from "../../../components/TableSkeleton";
import EmptyValue from "../../../components/EmptyValue";
import { formatCurrency } from "../../../utils/format";
import { DEFAULT_PAGE_SIZE } from "../../../constants/pagination";
// Data and writes are SHARED with the Finance console; only this page's chrome is not.
// A refund's lifecycle, and who may move it, must be identical in both places -- what
// differs is that Finance wraps its screens in ListPage plus a tab strip, while this
// console uses the legacy card vocabulary its own Orders, Quotations and Outstanding
// tabs use. One component could not serve both without one console looking foreign.
import useFinanceRefunds from "../../../finance/hooks/useFinanceRefunds";
import { useIssueRefund } from "../../../finance/hooks/useRefundActions";

// Sales Refunds -- /v2/sales/refunds.
//
// READ-ONLY ON APPROVAL. There is no Approve button here at any status: approving is
// gated to ADMIN/SUPER_ADMIN on the server (routes/api/payment.js), and sales is not an
// approver. Sales CAN initiate a refund once finance has approved it.
//
// Colour marks state that needs a person: awaiting approval, and failed. A refund that is
// in flight or already settled stays quiet, so the eye lands on the rows with work in
// them -- the same rule the Outstanding tab follows for money owed.

// ONE ANCHOR PER ROW. Weight is a scarce signal: when six of eleven columns were bold,
// none of them read as important. Only the name is bold -- it says who the row is about --
// and the approval pill keeps semibold because pill text is small and needs it to stay
// legible. Everything else carries its meaning through colour or alignment instead.
const PARAM = { page: "page" };

const NEEDS_ATTENTION = "#dc143c";
const SETTLED = "#208f7b";

const APPROVAL_PILL = {
    PENDING: "bg-[#fdeaee] text-[#dc143c]",
    APPROVED: "bg-elm-50 text-elm-700",
};

// Only the two ends of the lifecycle earn colour. REQUESTED/APPROVED/INITIATED/PROCESSING
// are all "in progress" and colouring them would make every row shout at once.
const STATUS_TONE = {
    FAILED: NEEDS_ATTENTION,
    SUCCESS: SETTLED,
};

const CONFIRM_ISSUE = (id) =>
    `Send refund ${id} to the payment gateway?\n\nThis moves the money and cannot be undone.`;

function SalesRefundsPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Math.max(Number(searchParams.get(PARAM.page)) || 1, 1);

    const issue = useIssueRefund();
    const query = useFinanceRefunds({ page, size: DEFAULT_PAGE_SIZE });

    const rows = query.data?.refunds || [];
    const total = query.data?.pagination?.total || 0;

    const goToPage = (nextPage) => {
        const next = new URLSearchParams(searchParams);
        next.set(PARAM.page, String(nextPage));
        setSearchParams(next, { replace: true });
    };

    // Columns follow the legacy refunds dashboard. Partner and Partner Entity ID are kept:
    // sales works partner-sourced orders, so knowing which partner a refund belongs to is
    // part of their job. Bank ARN and Quotation ID stay off -- those are bank-
    // reconciliation fields, and they remain on the Finance screen where that work happens.
    // Columns read left to right as a narrative: which refund -> whose it is -> how much
    // -> where it has got to -> its bank reference -> what you can do.
    //
    // Partner and Partner Entity ID sit with Order ID, Name and Phone because they answer
    // the same question -- whose order this refund is against. They were previously
    // stranded after Transaction ID, which answers nothing of the kind.
    //
    // Approval precedes Status because approval happens first and gates everything after
    // it: the pill gives the coarse answer, the status the detail. Bank ARN and Quotation
    // ID stay off this screen -- reconciliation fields, kept to the Finance console.
    const columns = [
        { key: "id", label: "Refund ID", render: (row) => row.id },

        // Whose refund this is.
        {
            key: "orderId",
            label: "Order ID",
            render: (row) => (row.orderId ? <span className="text-elm-600">{row.orderId}</span> : <EmptyValue />),
        },
        {
            key: "name",
            label: "Name",
            // The one bold thing in the row: it says who this is about.
            render: (row) => (row.name ? <span className="font-bold text-black">{row.name}</span> : <EmptyValue />),
        },
        { key: "phone", label: "Phone", render: (row) => row.phone || <EmptyValue /> },
        { key: "partner", label: "Partner", render: (row) => row.partner || <EmptyValue /> },
        { key: "partnerEntityId", label: "Partner Entity ID", render: (row) => row.partnerEntityId || <EmptyValue /> },

        // How much.
        {
            key: "amount",
            label: "Amount",
            align: "right",
            // Uncoloured: every row here is money going back to a customer, so tinting it
            // would colour the whole column and say nothing.
            render: (row) => (
                row.amount === null || row.amount === undefined
                    ? <EmptyValue />
                    : <span className="tabular-nums">{formatCurrency(row.amount, { fractionDigits: 2 })}</span>
            ),
        },

        // Where it has got to.
        {
            key: "approvalStatus",
            label: "Approval",
            render: (row) => (
                row.approvalStatus
                    ? (
                        <span className={`inline-block rounded-2xl px-3 py-1 font-semibold ${APPROVAL_PILL[row.approvalStatus] || ""}`}>
                            {row.approvalStatus === "PENDING" ? "Pending" : "Approved"}
                        </span>
                    )
                    : <EmptyValue />
            ),
        },
        {
            key: "status",
            label: "Status",
            render: (row) => (
                <span style={STATUS_TONE[row.status] ? { color: STATUS_TONE[row.status] } : undefined}>
                    {row.status}
                </span>
            ),
        },

        // Its bank reference, then what you can do about it.
        { key: "transactionId", label: "Transaction ID", render: (row) => row.transactionId || <EmptyValue /> },
        {
            key: "action",
            label: "Action",
            render: (row) => {
                // No Approve button at any status -- sales is not an approver, and saying
                // so plainly beats a disabled control that invites a click.
                if (row.approvalStatus === "PENDING") {
                    return <span className="text-gray-400">Awaiting approval</span>;
                }
                if (row.status !== "APPROVED") return <span className="text-gray-400">—</span>;

                return (
                    <button
                        type="button"
                        disabled={issue.isLoading}
                        className="rounded-2xl bg-black px-3 py-1.5 font-sans text-white-50 disabled:opacity-50"
                        onClick={() => {
                            // eslint-disable-next-line no-alert
                            if (window.confirm(CONFIRM_ISSUE(row.id))) issue.mutate(row.id);
                        }}
                    >
                        Initiate Refund
                    </button>
                );
            },
        },
    ];

    return (
        <PageContainer>
            <h2 className="text-2xl font-semibold mb-4">Refunds</h2>

            {/* isIdle matters in react-query v3: a disabled query with no data is idle,
                not loading, and would otherwise fall through to the empty state. */}
            {query.isLoading || query.isIdle ? (
                <div className="bg-white rounded-2xl p-4">
                    <table className="w-full">
                        <tbody>
                            <TableSkeleton rows={8} cols={columns.length} />
                        </tbody>
                    </table>
                </div>
            ) : query.isError ? (
                <EmptyState title="Couldn't load refunds" hint="The request failed. Refresh to try again." />
            ) : rows.length === 0 ? (
                <EmptyState title="No refunds yet" hint="Refunds raised against these orders will appear here." />
            ) : (
                <>
                    <DataTable columns={columns} rows={rows} />
                    <Pagination page={page} size={DEFAULT_PAGE_SIZE} total={total} onChange={goToPage} />
                </>
            )}
        </PageContainer>
    );
}

export default SalesRefundsPage;
